// ShipmentTable.tsx
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';

import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';

// Providers
import { ShipmentMutationsProvider, useShipmentMutations } from './providers/ShipmentMutationsProvider';
import { TableUIProvider, useTableUI } from './providers/TableUIProvider';

// Hooks
import { useCompanyProject } from './hooks/useCompanyProject';
import { useProjectShipmentData } from './hooks/useProjectShipmentData';
import { useTableData } from './hooks/useTableData';
import { useTableSettings } from './hooks/useTableSettings';
import { useShipmentEvents, useTrackingJob } from '@/core/api/queries';

// Config
import { registerCustomElements } from './config/handsontable';
import { createDynamicStyles } from './styles/tableStyles';

// Components
import TableToolbar from './components/TableToolbar';
import DebugShipmentDrawer from './components/DebugShipmentDrawer';
import ShipmentRouteDrawer from '@/components/shipment-route-drawer';
import ShareTableWizard from './ShareTableWizard';
import { ConfirmationModal } from '@/components/confirmation-modal';

// Utils
import useCheckIfProjectExists from '@/lib/useCheckIfProjectExists';
import { useSearchParams } from 'react-router-dom';

registerAllModules();
registerCustomElements();

const ShipmentTable: React.FC = () => {
  const [shareDataSidebarOpened, setShareDataSidebarOpened] = useState(false);
  const [routeDrawerOpened, setRouteDrawerOpened] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const [stylesReady, setStylesReady] = useState(false);
  const [searchParams] = useSearchParams();

  // Context hooks
  const {
    isSidebarOpened,
    selectedRow,
    setSidebarOpened,
    setSelectedRow,
    deleteConfirmModal,
    closeDeleteConfirmModal,
    confirmDelete,
    hotRef,
  } = useTableUI();

  const { handleDelete, handleFileUpload } = useShipmentMutations();

  // Core data hooks
  const { projectId, userId } = useCompanyProject();

  useCheckIfProjectExists(projectId);

  const { shipments, fields, trackingJobsMap } = useProjectShipmentData(projectId, false, searchParams);

  const { tableData, shipmentsMap } = useTableData(shipments, fields);

  const { settings, columns } = useTableSettings({
    fields,
    tableData,
    shipments: shipments || [],
    shipmentsMap,
    trackingJobsMap,
    hotRef,
    projectId: projectId!,
    userId: userId!,
  });

  const { data: shipmentEvents } = useShipmentEvents(selectedShipmentId || '');
  const { data: trackingJob } = useTrackingJob(selectedShipmentId || '');

  // Initialize styles
  useEffect(() => {
    if (projectId) {
      createDynamicStyles();
      requestAnimationFrame(() => setStylesReady(true));
    }
  }, [projectId]);

  // Update selected shipment ID
  useEffect(() => {
    if (selectedRow) setSelectedShipmentId(selectedRow?.rowData[0] || null);
  }, [selectedRow, shipments]);

  // Wrapped handlers
  const wrappedHandleDelete = useCallback((shipmentId: string) => handleDelete({ shipmentId }), [handleDelete]);

  const wrappedHandleFileUpload = useCallback(
    (file: File) => {
      if (!projectId) return Promise.resolve();
      return handleFileUpload({ file, projectId });
    },
    [handleFileUpload, projectId],
  );

  // Route drawer handler
  useEffect(() => {
    const handler = (e: any) => {
      const row = e?.detail?.row;
      if (typeof row !== 'number') return;
      const instance = hotRef.current?.hotInstance;
      const rowData = instance.getDataAtRow(row);
      setSelectedRow({ rowData, index: row });
      setRouteDrawerOpened(true);
    };
    window.addEventListener('open-shipment-route', handler as any);
    return () => window.removeEventListener('open-shipment-route', handler as any);
  }, [hotRef, tableData, setSelectedRow]);

  const currentShipment = selectedShipmentId ? shipmentsMap[selectedShipmentId] : null;

  const columnsWithoutId = useMemo(() => columns.filter((column) => column.data !== 'id'), [columns]);

  return (
    <div>
      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={closeDeleteConfirmModal}
        onConfirm={() => confirmDelete(wrappedHandleDelete)}
        title="Delete Shipment"
        message={`Are you sure you want to delete this shipment?\n\nReference: ${deleteConfirmModal.shipmentReference}\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <TableToolbar
        projectId={projectId}
        onFileUpload={wrappedHandleFileUpload}
        onShareClick={() => setShareDataSidebarOpened(true)}
      />

      {settings && (
        <div className={`transition-opacity duration-500 ease-in-out ${stylesReady ? 'opacity-100' : 'opacity-0'}`}>
          <HotTable
            ref={hotRef}
            {...settings}
            hiddenColumns={{ columns: [0], indicators: false }} // Hide ID column
            licenseKey="non-commercial-and-evaluation"
          />
        </div>
      )}

      <DebugShipmentDrawer
        isOpen={isSidebarOpened}
        onClose={() => setSidebarOpened(false)}
        selectedRow={selectedRow?.rowData}
        trackingJob={trackingJob}
        shipmentEvents={shipmentEvents}
        shipmentData={currentShipment}
        columns={columns}
      />

      <ShipmentRouteDrawer
        isOpen={routeDrawerOpened}
        onClose={() => setRouteDrawerOpened(false)}
        shipmentData={currentShipment}
        shipmentEvents={shipmentEvents}
      />

      <ShareTableWizard
        isOpen={shareDataSidebarOpened}
        columns={columnsWithoutId}
        tableData={tableData}
        onClose={() => setShareDataSidebarOpened(false)}
      />
    </div>
  );
};

export default () => (
  <TableUIProvider>
    <ShipmentMutationsProvider>
      <ShipmentTable />
    </ShipmentMutationsProvider>
  </TableUIProvider>
);
